import type { ItemFieldRow } from './item-field-reader';
import { createAbilityEssenceSourceCollector } from './ability-essence-sources';
import type { AbilityEssenceSourceDependencies } from './ability-essence-sources';
import { createFormulaReadContext } from './formula-read-context';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { scoreCarrierIntegerLayerArithmetic } from './formula-variable-resolution';
import { integerContributionSource } from './status-integer-contribution';
import type { IntegerContributionSource } from './status-integer-contribution';
import { phpJsonString } from './php-json-values';
import { phpAdd, phpJsonInteger, phpFloatToInteger } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const has = (v: unknown, key: string): boolean => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key);
const own = (v: unknown, key: string): unknown => has(v, key) ? (v as Row)[key] : undefined;
const requireArray = (v: unknown): ItemFieldRow => { if (!arrayLike(v)) throw new TypeError('Ancestral essence requires a PHP array'); return v; };
const stringArg = (v: unknown): string => { if (v == null || arrayLike(v)) throw new TypeError('Ancestral essence key requires a scalar string'); return phpJsonString(v); };
/** Native ancestral walk: gated passive contributions followed by authored
 * default-provider contributions. Each carrier is scored in its own context;
 * the ancestry projection deliberately drops the generic carrier category.
 */
export function createAncestralEssenceSourceCollector(deps: AbilityEssenceSourceDependencies) {
    const passives = createAbilityEssenceSourceCollector(deps), readers = createFormulaReadContext(deps);
    const providers = createCatalogDefaultProviderReader({catalogRows: deps.catalogRows, nodeItemId: readNodeItemId,
        resolveCarrierRef: id => { deps.trace?.(['reference', id]); return deps.resolveCarrierRef(id); }});
    function carriers(input: ItemFieldRow, rawKey: string): IntegerContributionSource[] {
        const host = requireArray(input), key = stringArg(rawKey), lines: IntegerContributionSource[] = [];
        for (const carrier of providers.providers(host)) {
            const layers = own(carrier, 'item_layer_types');
            if (!arrayLike(layers) || !Object.keys(layers).length || !has(layers, key)) continue;
            const score = scoreCarrierIntegerLayerArithmetic(carrier, key, 0, false, deps.metadata, readers);
            const value = typeof score === 'bigint' ? score : phpFloatToInteger(score); if (value === 0n) continue;
            const items = own(carrier, 'items'), type = own(items, 'item_type') ?? '';
            const kind = stringArg(type !== '' ? type : 'carrier');
            lines.push(integerContributionSource(kind, phpJsonString(own(items, 'item_name') ?? ''), value, phpJsonString(own(items, 'item_id') ?? ''), kind, null));
        }
        return lines;
    }
    function direct(input: ItemFieldRow, rawKey: string) {
        const host = requireArray(input), key = stringArg(rawKey), passive = passives.ancestralPassive(host, key), lines = [...passive.lines];
        for (const row of carriers(host, key)) lines.push({label: row.label, kind: row.kind, value: row.value, href_id: row.href_id});
        let total: PhpArithmetic = 0n; for (const row of lines) total = phpAdd(total, phpJsonInteger(row.value));
        return {rolled_total: Number(total), lines};
    }
    function display(input: ItemFieldRow, name: string) {
        const host = requireArray(input), key = 'ancestral_' + stringArg(name), result = direct(host, key);
        return {lines: result.lines, rolled_total: result.rolled_total, rolled_total_brace: null};
    }
    return {direct, display, carriers};
}
